import { TrashIcon } from "@heroicons/react/24/outline";
import { trim } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { DateValueType } from "react-tailwindcss-datepicker";
import { ListFieldsType } from "src/api/config";
import { EmailListFieldsRecordType, emailQueueListName } from "src/api/config/email.config";
import { myTaskMeetingsListName } from "src/api/config/meetings.config";
import {
  myTaskActionCommentsFilesListName,
  myTaskActionCommentsListName,
  myTaskActionsHistoryListName,
  myTaskActionsListName,
  myTaskActionsStatusesListName,
} from "src/api/config/myTasksActions.config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import Loading from "src/components/Loading";
import DefaultActionInMeetingTable from "src/components/tables/meetingTables/meetingsActionTable/DefaultActionTable";
import HashtagActionInMeetingTable from "src/components/tables/meetingTables/meetingsActionTable/HashtagActionInMeetingTable";
import ResponsibleActionInMeetingTable from "src/components/tables/meetingTables/meetingsActionTable/ResponsibleActionInMeetingTable";
import StatusActionInMeetingTable from "src/components/tables/meetingTables/meetingsActionTable/StatusActionInMeetingTable";
import { ArchiveAddIcon, ArchiveRemoveIcon } from "src/components/ui-icons/card";
import { QuitIcon } from "src/components/ui-icons/menu";
import { searchPeopleRequest } from "src/components/ui/form/peoplePicker/api";
import TGButton from "src/components/ui/TGButton";
import { TGCheckList } from "src/components/ui/TGCheckList";
import TGDatePicker from "src/components/ui/TGDatePicker";
import { TGDisclosure } from "src/components/ui/TGDisclosure";
import { TGRadioList } from "src/components/ui/TGRadioList";
import TGTag from "src/components/ui/TGTag";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { useGetSelect } from "src/hooks/redux-selectors";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import ExportToExcel from "src/modules/exelExport/exelExport";
import { deleteItem, getItems, updateItem } from "src/modules/spItems/actions";
import { deleteItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { modifiedItemsState } from "src/modules/spItems/selectors";
import { getUserProperties } from "src/modules/users/api";
import { selectCurrentUser } from "src/modules/users/selector";
import { filterUserSearchResults } from "src/pages/DashboardPage/acadia/components/Filters/ListsSearch";
import { LayoutWithAsideAndContent } from "src/pages/LayoutWithAsideAndContent";
import { CheckBoxListItem, RadioBoxListItem, SearchPeopleType, UserListItem } from "src/types/main";
import { emailAPI } from "src/utils/api.utils";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { getLocalItem, setLocalItem } from "src/utils/localStorage";
import { extractHashtags } from "src/utils/string.utils";
import { TooltipItem } from "src/utils/Tooltip";
import { getNameAndSurname, nameFromEmail, shortNameFromEmail } from "src/utils/users.utils";
import {
  buildOtherFieldExcelColumns,
  mapOtherFieldValuesToExcelRow,
} from "../../utils/otherFieldsExport.utils";
import AddActionInModal from "../../addAction/modal";
import { ActiveMeetingType, Participant, PersonType } from "../type";
import { SearchUI } from "./../../../../../components/ui/Search";
import { CreateMeetingForm, frequencyList, hasMultipleOwners, visibilityList } from "./CreateMeetingButton";
import { SearchInputEmployee } from "./SearchInputEmployee";
import { actionsStatus } from "src/api/config/systemSwitch";

type MeetingStateType = ListFieldsType[typeof myTaskMeetingsListName];

export function MeetingCard({
  zIndex,
  setOpen,
  open,
  meetingID,
}: {
  meetingID: string | null;
  zIndex: number;
  open: boolean;
  setOpen: (b: boolean) => void;
}) {
  const [meetingState, setMeetingState] = useState<MeetingStateType | null>(null);
  const [loading, setLoading] = useState(false);

  function changeMeetingState(meeting: MeetingStateType) {
    setMeetingState(meeting);
  }

  useAsyncEffect(async () => {
    try {
      setLoading(true);

      if (meetingID) {
        const { items } = await getItemsRequest({ listName: myTaskMeetingsListName, filters: { ID: meetingID } });

        const [meetingObj] = modifiedItemsState(items, myTaskMeetingsListName);

        setMeetingState(meetingObj);
      }
    } catch (error) {
      console.error(error);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [meetingID]);

  return (
    <DrawerComponent percentage="93" zIndex={zIndex} open={open} toggleDrawer={(t) => setOpen(t)}>
      {loading || !meetingState ? (
        <div className="flex justify-center items-center w-full">
          <Loading />
        </div>
      ) : (
        <Content toggleOpen={setOpen} meetingState={meetingState!} changeMeetingState={changeMeetingState} />
      )}
    </DrawerComponent>
  );
}

type NavTypes = "actions" | "configs";

export type UserRoleTypes = "owner" | "participant" | "non-participant" | null;

const navActiveStyles = { color: "darkblue", borderBottom: "2px solid darkblue" };

function getButtonsByRole(
  role: UserRoleTypes,
  isAdmin: boolean,
  meeting: MeetingStateType,
  changeStatusMeeting: (id: number, status: ActiveMeetingType | "remove") => void,
  quitFromMeeting: (meeting: MeetingStateType, role: UserRoleTypes) => void,
) {
  if (meeting.Status === "active") {
    if (role === "owner") {
      return (
        <>
          <FlexCol onClick={() => changeStatusMeeting(meeting.ID, "archive")}>
            <TooltipItem position="top-left" tooltipsText="Архивировать совещание">
              <ArchiveAddIcon />
            </TooltipItem>
          </FlexCol>
          <FlexCol onClick={() => quitFromMeeting(meeting, "owner")}>
            <TooltipItem position="top-left" tooltipsText="Покинуть совещание">
              <QuitIcon className={"w-6 h-6 hover:text-darkblue text-[#9CA3AF]"} />
            </TooltipItem>
          </FlexCol>
        </>
      );
    } else if (role === "participant") {
      return (
        <>
          {isAdmin && (
            <FlexCol onClick={() => changeStatusMeeting(meeting.ID, "archive")}>
              <TooltipItem position="top-left" tooltipsText="Архивировать совещание">
                <ArchiveAddIcon />
              </TooltipItem>
            </FlexCol>
          )}
          <FlexCol onClick={() => quitFromMeeting(meeting, "participant")}>
            <TooltipItem position="top-left" tooltipsText="Покинуть совещание">
              <QuitIcon className={"w-6 h-6 hover:text-darkblue text-[#9CA3AF]"} />
            </TooltipItem>
          </FlexCol>
        </>
      );
    } else if (role === "non-participant" && isAdmin) {
      return (
        <>
          <FlexCol onClick={() => changeStatusMeeting(meeting.ID, "archive")}>
            <TooltipItem position="top-left" tooltipsText="Архивировать совещание">
              <ArchiveAddIcon />
            </TooltipItem>
          </FlexCol>
        </>
      );
    } else {
      return null;
    }
  } else {
    if (isAdmin || role === "owner") {
      return (
        <>
          <FlexCol>
            <TGTag color="gray">В архиве</TGTag>
          </FlexCol>
          <FlexCol onClick={() => changeStatusMeeting(meeting.ID, "active")}>
            <TooltipItem position="top-left" tooltipsText="Разархивировать совещание">
              <ArchiveRemoveIcon />
            </TooltipItem>
          </FlexCol>
          <FlexCol onClick={() => changeStatusMeeting(meeting.ID, "remove")}>
            <TooltipItem position="top-left" tooltipsText="Удалить совещание">
              <TrashIcon width={24} height={24} className="hover:text-darkblue text-[#9CA3AF]" />
            </TooltipItem>
          </FlexCol>
        </>
      );
    } else {
      return null;
    }
  }
}

function removeWordWithComma(input: string, word: string) {
  return input
    .split(", ")
    .map(trim)
    .filter((item) => item !== word)
    .join(", ");
}

const defaultSubmit: SubmitFiltersType = {
  createdDate: { start: null, end: null },
  completedDate: { start: null, end: null },
  responsible: null,
  hashtags: [],
  statuses: [],
  searchQuery: "",
};

const { createEmailMeetingBodyByTemplate, sendEmail } = emailAPI();

export const meetingCardScrollStorageKey = "meeting-table-scroll-position";

function Content({
  meetingState,
  toggleOpen,
  changeMeetingState,
}: {
  meetingState: MeetingStateType;
  toggleOpen: (b: boolean) => void;
  changeMeetingState: (meetingState: MeetingStateType) => void;
}) {
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector((state) => selectCurrentUser(state));

  const [userRole, setUserRole] = useState<UserRoleTypes>("non-participant"); // роль сотрудника в совещания

  const [cardNavigate, setCardNavigate] = useState<NavTypes>("actions");

  const [quitLoading, setQuitLoading] = useState(false);

  const [openQuitModal, setOpenQuitModal] = useState(false);
  const [openWarningArchiveModal, setOpenWarningArchiveModal] = useState(false);

  const [openWarningLeaveParticipantModal, setOpenWarningLeaveParticipantModal] = useState(false);

  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnArchiveModal, setOpenUnArchiveModal] = useState(false);
  const [openWarningQuitModal, setOpenWarningQuitModal] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [groupType, setGroupType] = useState<RadioBoxListItem | null>({ id: 1, title: "По статусам", type: "status" });

  const [submitFilters, setSubmitFilters] = useState<SubmitFiltersType>(defaultSubmit);

  function resetSubmitFilters() {
    setSubmitFilters(defaultSubmit);
  }

  function changeActiveNavigate(active: NavTypes) {
    setCardNavigate(active);
  }
  function changeGroupType(item: RadioBoxListItem) {
    setGroupType(item);
    sessionStorage.removeItem(meetingCardScrollStorageKey);
  }

  function resetGroupFilter() {
    setGroupType(null);
    sessionStorage.removeItem(meetingCardScrollStorageKey);
  }

  async function changeStatusMeeting(id: number, status: ActiveMeetingType | "remove") {
    try {
      const { items } = await getItemsRequest({ listName: myTaskActionsListName, filters: { Meeting: id } });

      if (status === "archive") {
        const isActionNotFinished = items.some(
          (item) =>
            item.Status.get_lookupId().toString() === actionsStatus.notStart.toString() ||
            item.Status.get_lookupId().toString() === actionsStatus.process.toString() ||
            item.Status.get_lookupId().toString() === actionsStatus.old.toString(),
        );

        if (items.length && isActionNotFinished) {
          setOpenWarningArchiveModal(true);
        } else {
          setOpenArchiveModal(true);
        }
      } else if (status === "active") {
        setOpenUnArchiveModal(true);
      } else if (status === "remove") {
        setOpenRemoveModal(true);
      }
    } catch (error) {
      console.error("error", error);
    }
  }

  async function submitRemoveMeeting() {
    try {
      // получить действия
      const actionsResponse = await getItemsRequest({
        listName: myTaskActionsListName,
        filters: {
          Meeting: meetingState.ID,
        },
      });

      if (actionsResponse.items.length) {
        // получить истории действий по ids
        const actionsHistoryResponse = await getItemsRequest({
          listName: myTaskActionsHistoryListName,
          filters: {
            Action: actionsResponse.items,
          },
        });

        // удалить истории действий по ids
        if (actionsHistoryResponse.items.length) {
          await deleteItemRequest({
            listName: myTaskActionsHistoryListName,
            ID: 0,
            Ids: actionsHistoryResponse.items,
          });
        }

        // получить комментарии действий по ids
        const actionsCommentsResponse = await getItemsRequest({
          listName: myTaskActionCommentsListName,
          filters: {
            Action: actionsResponse.items,
          },
        });

        if (actionsCommentsResponse.items.length) {
          // получить файлы комментарий по ids
          const actionsCommentFilesResponse = await getItemsRequest({
            listName: myTaskActionCommentsFilesListName,
            filters: {
              Comment: actionsCommentsResponse.items,
            },
          });

          // удалить файлы комментарий по ids
          if (actionsCommentFilesResponse.items.length) {
            await deleteItemRequest({
              listName: myTaskActionCommentsFilesListName,
              ID: 0,
              Ids: actionsCommentFilesResponse.items,
              fileField: "File",
            });
          }

          // удалить комментарии действий по ids
          await deleteItemRequest({
            listName: myTaskActionCommentsListName,
            ID: 0,
            Ids: actionsCommentsResponse.items,
          });
        }

        // удалить действия по ids
        await deleteItemRequest({ listName: myTaskActionsListName, ID: 0, Ids: actionsResponse.items });
      }

      // удалить совещание и обновить по id
      dispatch(deleteItem({ listName: myTaskMeetingsListName, ID: meetingState.ID, updateReducer: true }));
    } catch (error) {
      console.error(error);
    } finally {
      setOpenRemoveModal(false);
      toggleOpen(false);
    }
  }

  async function submitArchiveMeeting() {
    dispatch(
      updateItem({ listName: myTaskMeetingsListName, body: { ID: meetingState.ID, Status: "archive" }, update: true }),
    );

    const { archiveMeeting } = createEmailMeetingBodyByTemplate();

    const emailsOwnersData = meetingState.Owners.split(", ").reduce((acc: Promise<any>[], participant: string) => {
      const userName = nameFromEmail(participant);

      if (participant.trim().toLowerCase() !== currentUser.email.trim().toLowerCase()) {
        const createMeetingUserEmail = archiveMeeting(
          { title: meetingState.Title, id: meetingState.ID },
          nameFromEmail(currentUser.email),
          userName,
        );

        const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
          Body: createMeetingUserEmail,
          DateToBeSentAt: new Date(),
          Title: "Совещание",
          Subject: "Мои Задачи |  Архивирование совещания",
          Recepients: participant.toLowerCase(),
        };

        acc.push(sendEmail(emailBody));
      }

      return acc;
    }, [] as Promise<any>[]);

    const emailsParticipantsData =
      meetingState.Participants && meetingState.Participants.length
        ? meetingState.Participants.split(", ").reduce((acc: Promise<any>[], participant: string) => {
            const userName = nameFromEmail(participant);

            if (participant.toLowerCase() !== currentUser.email.toLowerCase()) {
              const createMeetingUserEmail = archiveMeeting(
                { title: meetingState.Title, id: meetingState.ID },
                nameFromEmail(currentUser.email),
                userName,
              );

              const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
                Body: createMeetingUserEmail,
                DateToBeSentAt: new Date(),
                Title: "Совещание",
                Subject: "Мои Задачи | Архивирование совещания",
                Recepients: participant.toLowerCase(),
              };

              acc.push(sendEmail(emailBody));
            }
            return acc;
          }, [] as Promise<any>[])
        : [];

    const allPromises = [...emailsOwnersData, ...emailsParticipantsData];

    await Promise.all(allPromises);

    setOpenArchiveModal(false);
    toggleOpen(false);
  }

  function submitUnArchiveMeeting() {
    dispatch(
      updateItem({ listName: myTaskMeetingsListName, body: { ID: meetingState.ID, Status: "active" }, update: true }),
    );

    setOpenUnArchiveModal(false);
    toggleOpen(false);
  }

  async function quitFromMeeting(meeting: MeetingStateType, role: UserRoleTypes) {
    const { items } = await getItemsRequest({
      listName: myTaskActionsListName,
      filters: { Meeting: meetingState.ID },
    });

    const isActionNotFinished = items.some(
      (item) =>
        (item.Status.get_lookupId().toString() === actionsStatus.notStart.toString() &&
          item.ResponsibleEmail === currentUser.email) ||
        (item.Status.get_lookupId().toString() === actionsStatus.process.toString() &&
          item.ResponsibleEmail === currentUser.email) ||
        (item.Status.get_lookupId().toString() === actionsStatus.old.toString() &&
          item.ResponsibleEmail === currentUser.email),
    );

    if (role === "owner") {
      if (meeting.Owners.split(", ").length > 1) {
        setOpenQuitModal(true);
      } else {
        setOpenWarningQuitModal(true);
      }
    } else {
      if (!isActionNotFinished) {
        setOpenQuitModal(true);
      } else {
        setOpenWarningLeaveParticipantModal(true);
      }
    }
  }

  async function submitLeaveMeeting() {
    if (userRole === "owner") {
      const bodyOwner = removeWordWithComma(meetingState.Owners, currentUser.email);

      dispatch(
        updateItem({
          listName: myTaskMeetingsListName,
          body: { ID: meetingState.ID, Owners: bodyOwner },
          update: true,
        }),
      );
    } else if (userRole === "participant") {
      const bodyParticipant = meetingState.Participants
        ? removeWordWithComma(meetingState.Participants, currentUser.email)
        : "";

      dispatch(
        updateItem({
          listName: myTaskMeetingsListName,
          body: { ID: meetingState.ID, Participants: bodyParticipant },
          update: true,
        }),
      );
    }

    const { quitParticipantFromMeeting } = createEmailMeetingBodyByTemplate();

    const emailsOwnersData = meetingState.Owners.split(", ").reduce((acc: Promise<any>[], participant: string) => {
      const userName = nameFromEmail(participant);

      if (participant.toLowerCase() !== currentUser.email.toLowerCase()) {
        const createMeetingUserEmail = quitParticipantFromMeeting(
          { title: meetingState.Title, id: meetingState.ID },
          nameFromEmail(currentUser.email),
          userName,
        );

        const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
          Body: createMeetingUserEmail,
          DateToBeSentAt: new Date(),
          Title: "Совещание",
          Subject: "Мои Задачи | Выход сотрудника из совещания",
          Recepients: participant.toLowerCase(),
        };

        acc.push(sendEmail(emailBody));
      }
      return acc;
    }, [] as Promise<any>[]);

    const emailsParticipantsData = meetingState.Participants.length
      ? meetingState.Participants.split(", ").reduce((acc: Promise<any>[], participant: string) => {
          const userName = nameFromEmail(participant);

          if (participant.toLowerCase() !== currentUser.email.toLowerCase()) {
            const createMeetingUserEmail = quitParticipantFromMeeting(
              { title: meetingState.Title, id: meetingState.ID },
              nameFromEmail(currentUser.email),
              userName,
            );

            const emailBody: Partial<EmailListFieldsRecordType[typeof emailQueueListName]> = {
              Body: createMeetingUserEmail,
              DateToBeSentAt: new Date(),
              Title: "Совещание",
              Subject: "Мои Задачи | Выход сотрудника из совещания",
              Recepients: participant.toLowerCase(),
            };

            acc.push(sendEmail(emailBody));
          }
          return acc;
        }, [] as Promise<any>[])
      : [];

    const allPromises = [...emailsOwnersData, ...emailsParticipantsData];

    setQuitLoading(true);
    try {
      await Promise.all(allPromises);
    } catch (error) {
      console.error("Ошибка при отправке писем:", error);
    } finally {
      setQuitLoading(false);
      setOpenQuitModal(false);
      toggleOpen(false);
    }
  }

  useEffect(() => {
    if (meetingState.Owners.includes(currentUser.email)) {
      setUserRole("owner");
    } else if (meetingState.Participants && meetingState.Participants.includes(currentUser.email)) {
      setUserRole("participant");
    } else {
      setUserRole("non-participant");
    }
  }, [currentUser, JSON.stringify(meetingState)]);

  return (
    <>
      <header className="flex flex-row justify-between items-center px-10 pt-10 pb-8 space-x-5 text-black">
        <FlexCol>
          <h1>{meetingState.Title}</h1>
        </FlexCol>
        <FlexCol>
          <FlexRow className="space-x-2.5 items-center">
            <ButtonRenderer
              userRole={userRole}
              currentUser={currentUser}
              meetingState={meetingState}
              changeStatusMeeting={changeStatusMeeting}
              quitFromMeeting={quitFromMeeting}
            />
          </FlexRow>
        </FlexCol>
      </header>
      <nav className="px-10 border-b">
        <FlexRow className="space-x-10">
          <FlexCol
            onClick={() => changeActiveNavigate("actions")}
            style={cardNavigate === "actions" ? navActiveStyles : {}}
            className="font-medium cursor-pointer py-[15px] transition ease-linear"
          >
            Действия
          </FlexCol>
          {currentUser.isAdmin || userRole !== "non-participant" ? (
            <FlexCol
              style={cardNavigate === "configs" ? navActiveStyles : {}}
              onClick={() => changeActiveNavigate("configs")}
              className="font-medium cursor-pointer py-[15px] transition ease-linear"
            >
              Настройки
            </FlexCol>
          ) : null}
        </FlexRow>
      </nav>

      {cardNavigate === "actions" ? (
        <LayoutWithAsideAndContent
          aside={
            <FlexCol className="h-full overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-md scrollbar-thumb-[#D1D5DB]">
              <TGDisclosure title="Группировка" defaultOpen={true}>
                <TGRadioList
                  item={groupType}
                  list={[
                    { id: 1, title: "По статусам", type: "status" },
                    { id: 2, title: "По ответственным", type: "responsible" },
                    { id: 3, title: "По хештегам", type: "hashtag" },
                  ]}
                  onChecked={changeGroupType}
                />
                <SubResetFilter resetFilter={resetGroupFilter} />
              </TGDisclosure>
              <ActionFilters
                submitFilters={submitFilters}
                resetSubmitFilters={resetSubmitFilters}
                changeSubmitFilters={(e) => setSubmitFilters(e)}
              />
            </FlexCol>
          }
          content={
            <FlexRow>
              <ActionsContent
                meetingState={meetingState}
                role={userRole}
                submitFilters={submitFilters}
                groupType={groupType}
                changeSubmitFilters={(e) => setSubmitFilters(e)}
              />
            </FlexRow>
          }
        />
      ) : (
        <ConfigForm meetingState={meetingState} role={userRole} changeMeetingState={changeMeetingState} />
      )}
      {/* Выйти из совещания */}
      <Modal
        zIndex={70}
        open={openQuitModal}
        toggleDialog={(state) => setOpenQuitModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы уверены, что хотите покинуть совещание?</p>
          <p>Добавлять участников может только владелец совещанния.</p>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenQuitModal(false)}>
            Отмена
          </TGButton>
          <TGButton disabled={quitLoading} type="button" color="bluedark" onClick={submitLeaveMeeting}>
            Покинуть совещание
          </TGButton>
        </footer>
      </Modal>
      {/* Ошибка выхода из совещания */}
      <Modal
        zIndex={70}
        open={openWarningQuitModal}
        toggleDialog={(state) => setOpenWarningQuitModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Невозможно покинуть совещание</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы единственный владелец совещания. Назначьте другого участника владельцем, чтобы покинуть совещание.</p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenWarningQuitModal(false)}>
            Закрыть
          </TGButton>
        </footer>
      </Modal>
      {/* Ошибка архивирования совещания */}
      <Modal
        zIndex={70}
        open={openWarningLeaveParticipantModal}
        toggleDialog={(state) => setOpenWarningLeaveParticipantModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Невозможно покинуть совещание</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>У вас остались незакрытые действия.</p>
          <p>
            Закройте или отмените действия, либо назначьте другого
            <br /> ответственного, чтобы покинуть совещание.
          </p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenWarningLeaveParticipantModal(false)}>
            Закрыть
          </TGButton>
        </footer>
      </Modal>
      {/* Ошибка архивирования совещания */}
      <Modal
        zIndex={70}
        open={openWarningArchiveModal}
        toggleDialog={(state) => setOpenWarningArchiveModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Невозможно архивировать совещание</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>В совещании остались незакрытые действия.</p>
          <p>Закройте или отмените действия, чтобы архивировать совещание.</p>
        </div>

        <footer className="flex justify-center px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenWarningArchiveModal(false)}>
            Закрыть
          </TGButton>
        </footer>
      </Modal>
      {/* Архивирование совещания */}
      <Modal
        zIndex={70}
        open={openArchiveModal}
        toggleDialog={(state) => setOpenArchiveModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы уверены, что хотите архивировать совещание?</p>
          <p>Участники совещания не смогут работать с действиями.</p>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenArchiveModal(false)}>
            Отмена
          </TGButton>
          <TGButton type="button" color="bluedark" onClick={submitArchiveMeeting}>
            Архивировать
          </TGButton>
        </footer>
      </Modal>
      {/* Разархивирование совещания */}
      <Modal
        zIndex={70}
        open={openUnArchiveModal}
        toggleDialog={(state) => setOpenUnArchiveModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Разархивация совещания</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы уверены, что хотите разархивировать совещание?</p>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenUnArchiveModal(false)}>
            Отмена
          </TGButton>
          <TGButton type="button" color="bluedark" onClick={submitUnArchiveMeeting}>
            Разархивировать
          </TGButton>
        </footer>
      </Modal>
      {/* Удаление совещания */}
      <Modal
        zIndex={70}
        open={openRemoveModal}
        toggleDialog={(state) => setOpenRemoveModal(state)}
        modalClassName="min-h-0 max-w-[600px]"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <p>Вы уверены, что хотите удалить совещание?</p>
          <p>Все действия совещания тоже будут удалены.</p>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton type="button" onClick={() => setOpenRemoveModal(false)}>
            Отмена
          </TGButton>
          <TGButton type="button" color="bluedark" onClick={submitRemoveMeeting}>
            Удалить
          </TGButton>
        </footer>
      </Modal>
    </>
  );
}

interface ButtonRendererProps {
  userRole: UserRoleTypes;
  currentUser: { isAdmin: boolean };
  meetingState: any;
  changeStatusMeeting: (id: number, status: ActiveMeetingType | "remove") => Promise<void>;
  quitFromMeeting: (meeting: MeetingStateType, role: UserRoleTypes) => Promise<void>;
}

const ButtonRenderer: React.FC<ButtonRendererProps> = ({
  userRole,
  currentUser,
  meetingState,
  changeStatusMeeting,
  quitFromMeeting,
}) => {
  const buttons = useMemo(() => {
    return getButtonsByRole(userRole, currentUser.isAdmin, meetingState, changeStatusMeeting, quitFromMeeting);
  }, [userRole, currentUser.isAdmin, meetingState, changeStatusMeeting, quitFromMeeting]);

  return <>{currentUser.isAdmin || userRole !== "non-participant" ? buttons : null}</>;
};

function ConfigForm({
  meetingState,
  role,
  changeMeetingState,
}: {
  meetingState: MeetingStateType;
  role: UserRoleTypes;
  changeMeetingState: (meetingState: MeetingStateType) => void;
}) {
  const currentUser = useAppSelector((state) => selectCurrentUser(state));
  const [query, setQuery] = useSearchParams();
  const frequency = frequencyList.find((f) => f.name.includes(meetingState.Frequency));
  const visibility = meetingState.isPrivate ? visibilityList[1] : visibilityList[0];

  const [participants, setParticipants] = useState<Participant[]>([]);

  function toggleMeetingFormDialog(value: boolean) {
    if (!value) {
      query.delete("meetingCard");

      setQuery(query);
    }
  }

  async function getParticipants(participants: Participant[]) {
    const allParticipants: Participant[] = [];

    for (const participant of participants) {
      let personalNumber = getLocalItem(participant.user.Email);

      if (!personalNumber || personalNumber === "Нет номера") {
        const data = await getUserProperties(participant.user.Email, ["abiGlobalID"]);

        if (data?.abiGlobalID) {
          personalNumber = data.abiGlobalID;
          setLocalItem(participant.user.Email, personalNumber);
        } else {
          const data = await getUserProperties(participant.user.Email, ["apiGlobalID"]);
          if (data?.apiGlobalID) {
            personalNumber = data.apiGlobalID;
            setLocalItem(participant.user.Email, personalNumber);
          } else {
            const data = await getUserProperties(participant.user.Email, ["UserName"]);
            if (data?.UserName) {
              personalNumber = data.UserName;
              setLocalItem(participant.user.Email, personalNumber);
            } else {
              personalNumber = "Нет номера";
            }
          }
        }
      }

      const isOwner = participant.role[0].id === "1";
      const shouldDisable = isOwner && !hasMultipleOwners(participants) && !!participant.user;

      const dataResponse = await getUserProperties(participant.user.Email, ["PreferredName"]);

      const participantWithPersonalNumber = {
        ...participant,
        isDisabled: shouldDisable,
        user: {
          ...participant.user,
          ShortName: getNameAndSurname(dataResponse?.PreferredName, participant.user.Email, true),
          Title: dataResponse?.PreferredName
            ? getNameAndSurname(dataResponse?.PreferredName, participant.user.Email) + " (" + personalNumber + ")"
            : participant.user.Title + " (" + personalNumber + ")",
        },
      };

      allParticipants.push(participantWithPersonalNumber);
    }

    return allParticipants;
  }

  function submitResultForm(result: FieldValues) {
    const { Owners, Participants } = result.participants.reduce(
      (acc: any, p: any) => {
        if (p.role[0].id === "2") {
          acc.Participants = acc.Participants.length
            ? acc.Participants + ", " + p.user.Email
            : acc.Participants + p.user.Email;
        } else {
          acc.Owners = acc.Owners.length ? acc.Owners + ", " + p.user.Email : acc.Owners + p.user.Email;
        }
        return acc;
      },
      { Owners: "", Participants: "" },
    );

    const newMeetingState: MeetingStateType = {
      ...meetingState,
      Title: result.name,
      Frequency: result.frequency[0].name,
      Location: { ID: result.location[0].id, Title: result.location[0].name, Metadata: result.location[0].metadata },
      Department: {
        ID: result.department[0].id,
        Title: result.department[0].name,
        Metadata: result.location[0].metadata,
      },
      isPrivate: result.visibility[0].id === "2",
      Tor: JSON.stringify(result.tor),
      Participants: Participants,
      Owners: Owners,
      AdditionalFields: JSON.stringify(result.additionalFields),
    };

    changeMeetingState(newMeetingState);
  }

  useAsyncEffect(async () => {
    const participantsData = meetingState.Participants
      ? meetingState.Participants.split(", ").map((e) => ({
          id: e,
          user: {
            Avatar: null,
            ShortName: shortNameFromEmail(e),
            Email: e,
            Title: nameFromEmail(e),
            ID: 0,
            Department: "",
            Position: "",
            UserID: 0,
          },
          role: [{ id: "2", name: "Участник" }],
        }))
      : [];

    const ownersData = meetingState.Owners.split(", ").map((e) => ({
      id: e,
      user: {
        Avatar: null,
        ShortName: shortNameFromEmail(e),
        Email: e,
        Title: nameFromEmail(e),
        ID: 0,
        Department: "",
        Position: "",
        UserID: 0,
      },
      role: [{ id: "1", name: "Владелец" }],
    }));

    if (participantsData.length || ownersData.length) {
      const meetingParticipants = [...ownersData, ...participantsData];

      // const userEmails = all.map((user) => user.user.Email);

      // dispatch(addUsers(userEmails));
      const all = await getParticipants(meetingParticipants as Participant[]);

      setParticipants(all);
    }
  }, [meetingState.Participants, meetingState.Owners]);

  return (
    <div className="p-5 custom-form">
      {participants.length ? (
        <CreateMeetingForm
          onSubmitResult={submitResultForm}
          toggleDialog={toggleMeetingFormDialog}
          meetingID={meetingState.ID}
          type="edit"
          role={role}
          disabled={
            ((role === "non-participant" || role === "participant") && !currentUser.isAdmin) ||
            meetingState.Status === "archive"
          }
          defaultValues={{
            name: meetingState.Title,
            participants: participants,
            frequency: frequency ? [frequency] : [],
            location: [{ id: meetingState.Location.ID.toString(), name: meetingState.Location.Title }],
            department: [{ id: meetingState.Department.ID.toString(), name: meetingState.Department.Title }],
            visibility: visibility ? [visibility] : [],
            tor: JSON.parse(meetingState.Tor),
            additionalFields: JSON.parse(meetingState.AdditionalFields),
          }}
        />
      ) : (
        <Loading />
      )}
    </div>
  );
}

export type SubmitFiltersType = {
  createdDate: {
    start: Date | null;
    end: Date | null;
  };
  completedDate: {
    start: Date | null;
    end: Date | null;
  };
  responsible: null | PersonType;
  hashtags: CheckBoxListItem[];
  statuses: CheckBoxListItem[];
  searchQuery: string;
};

function ActionFilters({
  submitFilters,
  changeSubmitFilters,
  resetSubmitFilters,
}: {
  submitFilters: SubmitFiltersType;
  changeSubmitFilters: (e: SubmitFiltersType) => void;
  resetSubmitFilters: () => void;
}) {
  const { data: actions } = useGetSelect(myTaskActionsListName);
  const { data: myTaskActionsStatuses } = useGetSelect(myTaskActionsStatusesListName);

  const [allowedParticipants, setAllowedParticipants] = useState<string[]>([]);

  const [hashtagsList, setHashtagsList] = useState<CheckBoxListItem[]>([]);

  const { createdDate, completedDate, hashtags, responsible, statuses } = submitFilters;

  function changeCreatedDate(value: DateValueType) {
    const obj = {
      start: value!.startDate ? new Date(value!.startDate) : null,
      end: value!.endDate ? new Date(value!.endDate) : null,
    };

    changeSubmitFilters({ ...submitFilters, createdDate: obj });
  }

  function changeCompletedDate(value: DateValueType) {
    const obj = {
      start: value!.startDate ? new Date(value!.startDate) : null,
      end: value!.endDate ? new Date(value!.endDate) : null,
    };

    changeSubmitFilters({ ...submitFilters, completedDate: obj });
  }

  function changeStatuses(status: CheckBoxListItem) {
    let newValue = [...statuses];
    if (newValue.length > 0 && newValue.find((f) => f.id === status.id)) {
      const filtered = statuses.filter((n) => n.id !== status.id);

      newValue = filtered;
    } else {
      newValue.push(status);
    }

    changeSubmitFilters({ ...submitFilters, statuses: newValue });
  }

  function changeHashtag(hashtag: CheckBoxListItem) {
    let newValue = [...hashtags];
    if (newValue.length > 0 && newValue.find((f) => f.id === hashtag.id)) {
      const filtered = hashtags.filter((n) => n.id !== hashtag.id);

      newValue = filtered;
    } else {
      newValue.push(hashtag);
    }

    changeSubmitFilters({ ...submitFilters, hashtags: newValue });
  }

  function reset(key: keyof typeof submitFilters, value: any) {
    changeSubmitFilters({ ...submitFilters, [key]: value });
  }

  function changeResponsible(emp: PersonType | null) {
    changeSubmitFilters({ ...submitFilters, responsible: emp });
  }

  useEffect(() => {
    if (actions.length) {
      const actionHashtags = actions.reduce((acc, item) => {
        if (!item.Description) return acc;

        const hashtags = extractHashtags(item.Description);

        hashtags.forEach((tag) => {
          if (!acc.find((a) => a.id === tag)) {
            const option = { id: tag, title: tag };
            acc.push(option);
          }
        });

        return acc;
      }, [] as CheckBoxListItem[]);

      setHashtagsList(actionHashtags);

      const allowedParticipants: string[] = actions.map((item) => item.ResponsibleEmail);

      setAllowedParticipants(allowedParticipants);
    }
  }, [actions]);

  return (
    <>
      <TGDisclosure title="Дата создания" count={createdDate?.start ? 1 : 0}>
        <FlexCol className="relative z-[102] w-full gap-y-3.5">
          <h4 className={"font-normal"}>От и До</h4>
          <TGDatePicker
            value={{ startDate: createdDate.start, endDate: createdDate.end }}
            onChange={changeCreatedDate}
            popoverDirection="down"
          />
          <SubResetFilter resetFilter={() => reset("createdDate", { start: null, end: null })} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Срок выполнения" count={completedDate?.start ? 1 : 0}>
        <FlexCol className="relative z-[101] w-full gap-y-3.5">
          <h4 className={"font-normal"}>От и До</h4>
          <TGDatePicker
            value={{ startDate: completedDate.start, endDate: completedDate.end }}
            onChange={changeCompletedDate}
            popoverDirection="down"
          />
          <SubResetFilter resetFilter={() => reset("completedDate", { start: null, end: null })} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Ответственный" count={responsible ? 1 : 0}>
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchInputEmployee
            allowList={allowedParticipants}
            changePerson={changeResponsible}
            selectedPerson={responsible}
            classDropdown="relative rounded-none shadow-[0] scrollbar-thumb-rounded-lg text-[12px]"
          />
          <SubResetFilter resetFilter={() => reset("responsible", null)} />
        </FlexCol>
      </TGDisclosure>

      <TGDisclosure title="Статус действия" count={statuses.length}>
        <TGCheckList
          list={myTaskActionsStatuses.filter((f) => f.Status).map((m) => ({ id: m.ID, title: m.Title, rest: m }))}
          items={statuses}
          onChecked={changeStatuses}
        />
        <SubResetFilter resetFilter={() => reset("statuses", [])} />
      </TGDisclosure>
      <TGDisclosure title="Хештеги" count={hashtags.length}>
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          {/* <SearchUI
            placeholder="Найти..."
            defaultValue={searchHashtagQuery}
            onClick={handleHashtagSearch}
            search={true}
            resetButton={false}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          /> */}
          <TGCheckList list={hashtagsList} items={hashtags} onChecked={changeHashtag} />
          <SubResetFilter resetFilter={() => reset("hashtags", [])} />
        </FlexCol>
      </TGDisclosure>
      {/* <TGDisclosure title="Локация">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          <TGCheckList list={[]} items={[]} onChecked={() => {}} />
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Отдел">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          <TGCheckList list={[]} items={[]} onChecked={() => {}} />
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Участок">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          <TGCheckList list={[]} items={[]} onChecked={() => {}} />
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="ФИО сотрудника">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Введите имя сотрудника"
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            label={"ФИО сотрудника"}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          {/* <TGCheckList list={employees.employees} items={responsibles} onChecked={changeResponsibles} /> 
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Дата события">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <h4 className={"font-normal"}>От и До</h4>
          <TGDatePicker
            value={{ startDate: completedDate.start, endDate: completedDate.end }}
            onChange={changeCompletedDate}
            popoverDirection="down"
          />
          <SubResetFilter resetFilter={() => {}} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Контроль качества">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          <TGCheckList list={[]} items={[]} onChecked={() => {}} />
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Примечание">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Техника безопасности">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          <TGCheckList list={[]} items={[]} onChecked={() => {}} />
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure>
      <TGDisclosure title="Оценка качества">
        <FlexCol className="relative z-[100] w-full gap-y-3.5">
          <SearchUI
            placeholder="Найти..."
            defaultValue={searchQuery}
            onClick={handleSearch}
            search={true}
            resetButton={false}
            disabled={employees.loading}
            labelClass="text-black"
            inputClass="px-2.5 font-normal"
          />
          <TGCheckList list={[]} items={[]} onChecked={() => {}} />
          <SubResetFilter resetFilter={() => setResponsibles([])} />
        </FlexCol>
      </TGDisclosure> */}
    </>
  );
}

// function FilterUser() {
//   const [] = use;

//   return (
//     <>
//       <SearchUI
//         placeholder="Введите имя сотрудника"
//         defaultValue={searchQuery}
//         onClick={handleSearch}
//         search={true}
//         resetButton={false}
//         disabled={employees.loading}
//         label={"ФИО сотрудника"}
//         labelClass="text-black"
//         inputClass="px-2.5 font-normal"
//       />
//       <TGCheckUsersList list={employees.employees} items={responsibles} onChecked={changeResponsibles} />
//       <SubResetFilter resetFilter={() => setResponsibles([])} />
//     </>
//   );
// }

function useGetUsersBySearch(searchQuery: string) {
  const [error, setError] = useState(false);
  const [employees, setEmployees] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(false);

  async function searchItems(query: string) {
    setError(false);

    if (query === "" || query.length <= 3) {
      setEmployees(employees);
      return;
    }

    try {
      setLoading(true);
      const response = (await searchPeopleRequest(query.trim())) as unknown as SearchPeopleType[];

      if (response) {
        const dataList = filterUserSearchResults(response);
        if (dataList.length > 0) {
          setEmployees(dataList);
        } else {
          setEmployees([]);
        }
      }
    } catch (error) {
      console.error("ошибка поиска пользователя");
      setEmployees([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    searchItems(searchQuery);
  }, [searchQuery]);

  return { loading, employees, error };
}

export function SubResetFilter({ resetFilter }: { resetFilter: () => void }) {
  return (
    <FlexRow
      className="group items-center text-[12px] text-[#637381] cursor-pointer gap-x-2 flex-nowrap"
      onClick={resetFilter}
    >
      <FlexCol className="group-hover:underline">Сбросить фильтры</FlexCol>
      <FlexCol className="pt-1">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_33_4954)">
            <path
              d="M5.9998 5.39995L1.4248 0.824951C1.25605 0.656201 0.993555 0.656201 0.824805 0.824951C0.656055 0.993701 0.656055 1.2562 0.824805 1.42495L5.3998 5.99995L0.824805 10.575C0.656055 10.7437 0.656055 11.0062 0.824805 11.175C0.993555 11.3437 1.25605 11.3437 1.4248 11.175L5.9998 6.59995L10.5748 11.175C10.7436 11.3437 11.0061 11.3437 11.1748 11.175C11.2498 11.1 11.3061 10.9875 11.3061 10.875C11.3061 10.7625 11.2686 10.65 11.1748 10.575L6.5998 5.99995L11.1748 1.42495C11.2498 1.34995 11.3061 1.23745 11.3061 1.12495C11.3061 1.01245 11.2686 0.899951 11.1748 0.824951C11.0061 0.656201 10.7436 0.656201 10.5748 0.824951L5.9998 5.39995Z"
              fill="#637381"
            />
          </g>
          <defs>
            <clipPath id="clip0_33_4954">
              <rect width="12" height="12" fill="white" transform="translate(0 12) rotate(-90)" />
            </clipPath>
          </defs>
        </svg>
      </FlexCol>
    </FlexRow>
  );
}

const exportTempData = {
  columns: [
    { header: "Дата создания", key: "CreateDate", width: 30 },
    { header: "Описание", key: "Description", width: 50 },
    { header: "Срок выполнения", key: "DueDate", width: 30 },
    { header: "Статус", key: "Status", width: 20 },
    { header: "Ответственный", key: "Responsible", width: 30 },
  ],
  data: [],
};

function ActionsContent({
  role,
  meetingState,
  groupType,
  submitFilters,
  changeSubmitFilters,
}: {
  meetingState: MeetingStateType;
  role?: UserRoleTypes;
  groupType: RadioBoxListItem | null;
  submitFilters: SubmitFiltersType;
  changeSubmitFilters: (e: SubmitFiltersType) => void;
}) {
  const [query, setQuery] = useSearchParams();

  const { data: actions } = useGetSelect(myTaskActionsListName);

  const [counters, setCounters] = useState({
    all: 0,
    unstarted: 0,
    inProcess: 0,
    expired: 0,
  });

  useEffect(() => {
    const countStatuses = actions.reduce(
      (acc, cur) => {
        if (cur.Status.ID === 227) {
          acc.unstarted = acc.unstarted + 1;
        } else if (cur.Status.ID === 228) {
          acc.inProcess = acc.inProcess + 1;
        } else if (cur.Status.ID === 229) {
          acc.expired = acc.expired + 1;
        }

        return acc;
      },
      {
        all: 0,
        unstarted: 0,
        inProcess: 0,
        expired: 0,
      },
    );

    countStatuses.all = actions.length;

    setCounters(countStatuses);
  }, [actions]);

  const getModifiedActionsToExcel = useCallback(
    (actions: ListFieldsType[typeof myTaskActionsListName][]) => {
      const dynamicColumns = buildOtherFieldExcelColumns(meetingState.AdditionalFields);

      return {
        columns: [...exportTempData.columns, ...dynamicColumns],
        data: actions.map((action) => ({
          CreateDate: moment(action.Created).format("DD.MM.YYYY HH:mm"),
          Description: action.Description,
          DueDate: moment(action.DateForDone).format("DD.MM.YYYY HH:mm"),
          Status: action.Status.Title,
          Responsible: action.Responsible.Name,
          ...mapOtherFieldValuesToExcelRow(action.OtherFieldsValues, dynamicColumns),
        })),
      };
    },
    [meetingState.AdditionalFields],
  ) as (actions: ListFieldsType[typeof myTaskActionsListName][]) => void;

  return (
    <FlexCol className="w-full">
      <FlexRow className="space-x-7.5 pt-[17px] pb-[16px] justify-between border-b px-7.5">
        <FlexCol>
          <div className="text-[#5475E5] flex space-x-2.5 items-center px-7.5">
            <span className="rounded-full w-2.5 h-2.5 bg-[#5475E5]"></span>
            <span>Всего</span>
            <span className="text-[18px] font-medium">{counters.all}</span>
          </div>
        </FlexCol>
        <FlexCol>
          <div className="text-[#637381] flex space-x-2.5 items-center px-7.5">
            <span className="rounded-full w-2.5 h-2.5 bg-[#DEE2E6]"></span>
            <span>Не начато</span>
            <span className="text-[18px] font-medium">{counters.unstarted}</span>
          </div>
        </FlexCol>
        <FlexCol>
          <div className="text-[#F2A900] flex space-x-2.5 items-center px-7.5">
            <span className="rounded-full w-2.5 h-2.5 bg-[#F2A900]"></span>
            <span>В процессе</span>
            <span className="text-[18px] font-medium">{counters.inProcess}</span>
          </div>
        </FlexCol>
        <FlexCol>
          <div className="text-[#BA0C2F] flex space-x-2.5 items-center px-7.5">
            <span className="rounded-full w-2.5 h-2.5 bg-[#BA0C2F]"></span>
            <span>Просрочено</span>
            <span className="text-[18px] font-medium">{counters.expired}</span>
          </div>
        </FlexCol>
      </FlexRow>
      <FlexRow className="px-7.5 space-x-2.5 my-5">
        <FlexCol className="flex-1">
          <SearchUI
            onClick={(query) => changeSubmitFilters({ ...submitFilters, searchQuery: query })}
            placeholder="Найти по ключевым словам"
          />
        </FlexCol>
        <FlexCol className="shrink">
          <ExportToExcel
            exportData={getModifiedActionsToExcel(actions)}
            fileName={`meeting-${+query.get("meetingCard")!}`}
            type={"meeting"}
          />
        </FlexCol>
        <FlexCol className="shrink">
          {query.has("meetingCard") && role !== "non-participant" && meetingState.Status === "active" && (
            <AddActionInModal meetingID={+query.get("meetingCard")!} zIndex={70} percentage="79" />
          )}
        </FlexCol>
      </FlexRow>
      <TableByGroupType type={groupType?.type} submitFilters={submitFilters} />
    </FlexCol>
  );
}

function TableByGroupType({ type, submitFilters }: { type: string | undefined; submitFilters: SubmitFiltersType }) {
  const [query, setQuery] = useSearchParams();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (query.has("meetingCard")) {
      const meetingId = query.get("meetingCard");

      const allFilters: Partial<Record<keyof ListFieldsType[typeof myTaskActionsListName], any>> = {
        Meeting: [meetingId],
      };

      if (submitFilters.completedDate.start || submitFilters.completedDate.end) {
        let dateFiltersData: { start: string | null; end: string | null }[] = [];

        dateFiltersData.push({
          start:
            typeof submitFilters.completedDate.start === "string"
              ? moment(new Date(submitFilters.completedDate.start)).startOf("day").toISOString()
              : moment(submitFilters.completedDate.start).startOf("day").toISOString(),
          end:
            typeof submitFilters.completedDate.end === "string"
              ? moment(new Date(submitFilters.completedDate.end)).endOf("day").toISOString()
              : moment(submitFilters.completedDate.end).endOf("day").toISOString(),
        });

        allFilters.DateForDone = dateFiltersData[0];
      }

      if (submitFilters.createdDate.start || submitFilters.createdDate.end) {
        let dateFiltersData: { start: string | null; end: string | null }[] = [];

        dateFiltersData.push({
          start:
            typeof submitFilters.createdDate.start === "string"
              ? moment(new Date(submitFilters.createdDate.start)).startOf("day").toISOString()
              : moment(submitFilters.createdDate.start).startOf("day").toISOString(),
          end:
            typeof submitFilters.createdDate.end === "string"
              ? moment(new Date(submitFilters.createdDate.end)).endOf("day").toISOString()
              : moment(submitFilters.createdDate.end).endOf("day").toISOString(),
        });

        allFilters.Created = dateFiltersData[0];
      }

      if (submitFilters.responsible) {
        allFilters.ResponsibleEmail = submitFilters.responsible.Email.toLowerCase() + "*";
      }

      if (submitFilters.statuses.length) {
        allFilters.Status = submitFilters.statuses.map((s) => s.id);
      }

      if (submitFilters.hashtags.length) {
        allFilters.Description = submitFilters.hashtags.map((s) => s.title.trim() + "*");
      }

      if (submitFilters.searchQuery) {
        allFilters.Description = allFilters.Description
          ? [...allFilters.Description, submitFilters.searchQuery + "*"]
          : [submitFilters.searchQuery + "*"];
      }

      if (meetingId) {
        dispatch(
          getItems({
            listName: myTaskActionsListName,
            filters: allFilters,
            populateFields: ["Status", "Responsible"],
          }),
        );
      }
    }
  }, [submitFilters]);

  if (!type) return <DefaultActionInMeetingTable />;

  return (
    <>
      {type === "status" && <StatusActionInMeetingTable />}
      {type === "responsible" && <ResponsibleActionInMeetingTable />}
      {type === "hashtag" && <HashtagActionInMeetingTable />}
    </>
  );
}
